/**
 * Character offsets of a selection within its prose pane's rendered text.
 *
 * Measured against the rendered prose body's textContent, so they stay meaningful even
 * when the same words appear more than once. Returns null when the selection
 * isn't inside a prose pane.
 *
 * @returns {{start: number, end: number}|null}
 */
export function rangeOffsetsWithinProse(range) {
  const scope =
    range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentNode;
  const prose = scope && scope.closest ? scope.closest(".prose") : null;
  if (!prose) return null;

  try {
    const before = range.cloneRange();
    before.selectNodeContents(prose.querySelector(".prose-body") || prose);
    before.setEnd(range.startContainer, range.startOffset);
    const start = before.toString().length;
    const end = start + range.toString().length;
    return end > start ? { start, end } : null;
  } catch (e) {
    return null;
  }
}

/**
 * Wrap a selection in highlight spans, one per text node it touches.
 *
 * A selection that crosses a paragraph boundary cannot be wrapped in a single
 * span: extracting its contents yields partial <p> elements, and nesting those
 * inside an inline span both kills the highlight background and hands the
 * nested paragraph the `p:first-of-type` drop cap. Wrapping each text run
 * separately keeps every span inline and leaves the paragraph structure alone.
 *
 * @returns {HTMLSpanElement[]} the spans inserted, in document order. Callers
 *   record what was actually highlighted, so the payload and the reader agree
 *   when a selection overlaps text that already carries a tag.
 */
export function highlightRange(range, tagValue) {
  const doc = range.startContainer.ownerDocument;
  if (!doc) return [];

  const scope =
    range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentNode;
  if (!scope) return [];

  // Collect first: wrapping splits text nodes as we go.
  const walker = doc.createTreeWalker(scope, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
      // Never wrap text that already carries a tag. Nesting highlights renders
      // the two tags identically (so the reader cannot tell them apart) and
      // makes the innermost `data-tag` win for anything reading the DOM.
      // Overlapping selections tag only the untagged remainder.
      if (node.parentElement && node.parentElement.closest(".text-highlight")) return NodeFilter.FILTER_REJECT;
      return range.intersectsNode(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    },
  });
  const textNodes = [];
  let node = walker.nextNode();
  while (node) {
    textNodes.push(node);
    node = walker.nextNode();
  }

  const wrapped = [];
  textNodes.forEach((textNode) => {
    const start = textNode === range.startContainer ? range.startOffset : 0;
    const end = textNode === range.endContainer ? range.endOffset : textNode.nodeValue.length;
    if (start >= end) return;

    const slice = doc.createRange();
    slice.setStart(textNode, start);
    slice.setEnd(textNode, end);

    const span = doc.createElement("span");
    span.className = "text-highlight";
    span.dataset.tag = tagValue;
    span.setAttribute("tabindex", "0");
    try {
      // Safe: `slice` never leaves this single text node, so it cannot
      // partially select an element.
      slice.surroundContents(span);
      wrapped.push(span);
    } catch (e) {
      // Skip this run rather than losing the whole highlight.
    }
  });

  return wrapped;
}

/**
 * Group highlight spans into contiguous runs of rendered text, each with the
 * offsets and text of the run. Adjacent runs (a paragraph boundary, a bold
 * boundary) merge; a gap — text that already carried another tag — splits.
 *
 * @returns {{start: number, end: number, text: string}[]}
 */
export function highlightRuns(spans) {
  const runs = [];
  spans.forEach((span) => {
    const range = span.ownerDocument.createRange();
    range.selectNodeContents(span);
    const offsets = rangeOffsetsWithinProse(range);
    if (!offsets) return;
    const last = runs[runs.length - 1];
    if (last && last.end === offsets.start) {
      last.end = offsets.end;
      last.text += span.textContent;
    } else {
      runs.push({ ...offsets, text: span.textContent });
    }
  });
  return runs;
}

// Resolve saved offsets against the current DOM, after streaming or highlighting
// has replaced text nodes. Offsets exclude the feedback note and other UI.
//
// Offsets are measured against rendered text, and rendered text can shift
// underneath a saved range: when a streamed `**bold` phrase gets its closing
// `**`, both delimiters disappear from the rendering and everything after
// them moves left. So the saved words are the authority: if the offsets no
// longer land on exactly that text, leave it unresolved rather than guessing another occurrence. A selection whose words are gone stays unresolved rather than
// tagging a different passage.
export function rangeFromOffsets(prose, { start, end, text }) {
  if (!prose || !Number.isInteger(start) || !Number.isInteger(end) || start < 0 || end <= start) return null;
  const body = prose.querySelector(".prose-body") || prose;
  const exact = rangeAt(body, start, end);
  if (exact && exact.toString() === text) return exact;
  return null;
}

function rangeAt(prose, start, end) {
  const range = prose.ownerDocument.createRange();
  const walker = prose.ownerDocument.createTreeWalker(prose, NodeFilter.SHOW_TEXT);
  let offset = 0;
  let started = false;
  let node;
  while ((node = walker.nextNode())) {
    const next = offset + node.nodeValue.length;
    if (!started && start < next) {
      range.setStart(node, start - offset);
      started = true;
    }
    if (started && end <= next) {
      range.setEnd(node, end - offset);
      return range;
    }
    offset = next;
  }
  return null;
}
